import { LocalDateTime, LocalTime } from "@js-joda/core";
import assertNever from "assert-never";
import React from "react";
import { ClockInOutCallType } from "../../messages";
import { STRINGS } from "../../strings";
import { globalize, translate } from "../../translate";
import { AppButton } from "../Generic/AppButton";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import ModalHeadline from "../Generic/AppModal/components/ModalHeadline/ModalHeadline";

export interface PromptVisitActionSuccessModalProps {
  visitActionType: ClockInOutCallType;
  visitDurationMinutes: number;
  adjustedVisitStartTime: LocalDateTime | null;
  isOpen: boolean;
  onRequestClose: () => void;
}

const PromptVisitActionSuccessModal = (props: PromptVisitActionSuccessModalProps) => {
  const { title, messages, closeText } = (() => {
    const startedAt = props.adjustedVisitStartTime?.toLocalTime() ?? LocalTime.now();
    const formattedNow = globalize().formatLocalTime(startedAt, { time: "short" });
    switch (props.visitActionType) {
      case ClockInOutCallType.IN: {
        const nowPlusDuration = startedAt.plusMinutes(props.visitDurationMinutes);
        const formattedNowPlusDuration = globalize().formatLocalTime(nowPlusDuration, {
          time: "short",
        });
        return {
          title: `${translate(STRINGS.SHIFT_HAS_STARTED_IN)} ${formattedNow}`,
          messages: [
            `${translate(STRINGS.ESTIMATED_TIME_OF_CLOCKING_OUT_IS)} ${formattedNowPlusDuration}.`,
            translate(STRINGS.HAVE_A_PLEASANT_VISIT),
          ],
          closeText: translate(STRINGS.OK),
        };
      }
      case ClockInOutCallType.OUT: {
        return {
          title: `${translate(STRINGS.SHIFT_HAS_FINISHED_IN)} ${formattedNow}`,
          messages: [translate(STRINGS.GREAT_JOB)],
          closeText: translate(STRINGS.THANKS),
        };
      }
      default:
        assertNever(props.visitActionType);
    }
  })();

  return (
    <AppAbstractModal
      className="PromptVisitActionSuccessModal"
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
    >
      <div className="image" data-type={props.visitActionType} />

      <ModalHeadline>{title}</ModalHeadline>

      <div className="message">
        {messages.map((message, index) => (
          <p key={`message-${index}`}>{message}</p>
        ))}
      </div>

      <div className="actions">
        <AppButton variant="filled" color="primary" onClick={props.onRequestClose}>
          {closeText}
        </AppButton>
      </div>
    </AppAbstractModal>
  );
};

export default PromptVisitActionSuccessModal;
